import type {
  AnalyticsService,
  InsightsConfig,
  InsightsIdentity,
  TrackPageViewOptions,
} from "./types";
import * as datalayer from "./datalayer";
import * as mixpanel from "./mixpanel";
import * as posthog from "./posthog";
import * as logger from "./logger";
import { isLikelyAutomated } from "./bot-detection";

// The real implementation that will be used after initialization
export class InsightsService implements AnalyticsService {
  private debugMode: boolean = false;

  initInsights({
    mixpanelToken,
    mixpanelAutoCapture,
    mixpanelApiHost,
    posthogApiKey,
    posthogApiHost,
    debug = false,
  }: InsightsConfig): void {
    this.debugMode = !!debug;

    if (isLikelyAutomated()) {
      if (this.debugMode) {
        logger.debug(
          "InsightService: automated client detected, skipping insights initialization",
        );
      }
      return;
    }

    if (this.debugMode) {
      logger.debug("InsightService: Initializing insights");
    }

    try {
      mixpanel.initMixpanel(
        mixpanelToken,
        mixpanelAutoCapture,
        this.debugMode,
        mixpanelApiHost,
      );
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to initialize Mixpanel", e);
      }
    }

    try {
      posthog.initPosthog(posthogApiKey, posthogApiHost);
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to initialize Posthog", e);
      }
    }
  }

  enableDebugMode(): void {
    this.debugMode = true;
    logger.debug("Enabling debug mode");

    try {
      mixpanel.enableDebugMode();
      posthog.enableDebugMode();
    } catch (e) {
      logger.error("Failed to enable debug mode", e);
    }
  }

  disableDebugMode(): void {
    this.debugMode = false;
    logger.debug("Disabling debug mode");

    try {
      mixpanel.disableDebugMode();
      posthog.disableDebugMode();
    } catch (e) {
      logger.error("Failed to disable debug mode", e);
    }
  }

  identify(identity: InsightsIdentity): void {
    const { userId, accountId, organisationId, email, name, ...properties } =
      identity;

    // In very rare cases we might have a user without an account, so we'll
    // let null/undefined/blank strings through on that one
    if (!userId) {
      if (this.debugMode) {
        logger.warn("User ID not provided, skipping identify");
      }
      return;
    }

    if (this.debugMode) {
      logger.info("Identifying user", {
        userId,
        accountId,
        organisationId,
        email,
        name,
        ...properties,
      });
    }

    try {
      mixpanel.identify({
        userId,
        accountId,
        organisationId,
        email,
        name,
        ...properties,
      });
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to identify user in Mixpanel", e);
      }
    }

    try {
      posthog.identify({
        userId,
        accountId,
        organisationId,
        email,
        name,
        ...properties,
      });
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to identify user in Posthog", e);
      }
    }
  }

  trackPageView(options?: TrackPageViewOptions): void {
    const { excludeIds, includeDataLayer, ...properties } = options ?? {};

    if (this.debugMode) {
      logger.info("Tracking page view");
    }

    try {
      mixpanel.trackPageView({ excludeIds, ...properties });
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to track page view in Mixpanel", e);
      }
    }

    try {
      posthog.trackPageView(properties);
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to track page view in Posthog", e);
      }
    }

    if (includeDataLayer) {
      try {
        datalayer.trackPageView(properties);
      } catch (e) {
        if (this.debugMode) {
          logger.error("Failed to track page view in GTM", e);
        }
      }
    }
  }

  track(event: string, properties?: Record<string, unknown>): void {
    if (this.debugMode) {
      logger.info("Tracking event", { event, properties });
    }

    try {
      mixpanel.track(event, properties);
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to track event in Mixpanel", e);
      }
    }

    try {
      posthog.track(event, properties);
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to track event in Posthog", e);
      }
    }

    try {
      datalayer.track(event, properties);
    } catch (e) {
      if (this.debugMode) {
        logger.error("Failed to track event in Datalayer", e);
      }
    }
  }

  setupObserver(): () => void {
    // Helper to get all data-insight-* attributes from an element
    const getInsightAttributes = (
      element: HTMLElement,
    ): { event?: string; [key: string]: string | undefined } => {
      // limit how many data attributes we'll process
      const MAX_ATTRIBUTES = 10;
      let count = 0;

      const attributes: { event?: string; [key: string]: string | undefined } =
        {};

      for (const attr of Array.from(element.attributes)) {
        if (count >= MAX_ATTRIBUTES) break;
        if (attr.name.startsWith("data-insight-")) {
          // Validate attribute name format
          if (!/^data-insight-[a-zA-Z0-9-]+$/.test(attr.name)) continue;

          // Sanitize attribute value
          if (typeof attr.value !== "string" || attr.value.length > 100)
            continue;

          // Convert data-insight-event-name to eventName
          const key = attr.name
            .replace("data-insight-", "")
            .split("-")
            .map((part: string, index: number) =>
              index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1),
            )
            .join("");
          attributes[key] = attr.value;
          count++;
        }
      }
      return attributes;
    };

    // Helper to find closest element with data-insight attributes
    const findClosestElementWithInsights = (element: HTMLElement) => {
      let current = element;
      while (current && current !== document.body) {
        const insights = getInsightAttributes(current);
        if (Object.keys(insights).length > 0) {
          return insights;
        }

        current = current.parentElement as HTMLElement;
      }
      return null;
    };

    // Global click handler
    const handleClick = (event: MouseEvent): void => {
      if (!(event.target instanceof HTMLElement)) return;
      const insights = findClosestElementWithInsights(event.target);
      if (insights) {
        // Extract special properties if they exist
        const { event: eventName, ...properties } = insights;
        this.track(eventName || "element_clicked", properties);
      }
    };

    // Add listener to document body to catch all clicks
    document.body.addEventListener("click", handleClick);

    // Return cleanup function in case it's needed
    return () => {
      document.body.removeEventListener("click", handleClick);
    };
  }
}
